import React from "react";
import clsx from "clsx";
import { Link, Text, Tooltip } from "@merge-api/merge-javascript-shared";
import { Info } from "lucide-react";
import { ScopeOption } from "../../../../../../models/CommonModel";
import ScopePermissionSwitch from "./ScopePermissionSwitch";

interface Props {
  /** Monospace field name shown in the first column (e.g. "remote_data"). */
  fieldName: string;
  /** Type label shown in the second column (e.g. "object"). */
  typeLabel: string;
  /** Description shown in the third column; omit for rows that only carry an info icon. */
  description?: string;
  /** Explanatory text shown on the info icon next to the label; omit to hide the icon. */
  infoText?: string;
  /** Docs link the info icon opens on click. */
  infoHref?: string;
  scope: ScopeOption;
  isViewing: boolean;
  disabled: boolean;
  disabledTooltip?: React.ReactNode;
  /** When set (e.g. on a deprecated model), replaces the description with the deprecation message. */
  deprecationMessage?: React.ReactNode;
  /** Past-dated deprecation: dims the text columns to 60% (switch stays full opacity), like a field. */
  isPastDeprecation?: boolean;
  onChange: (scope: ScopeOption) => void;
  /** Indents the row when the remote toggle belongs to a nested sub-object. */
  nested?: boolean;
}

/**
 * Standalone Remote Data / Remote Fields toggle row. Mirrors ScopeFieldRow's layout but is
 * always binary (no Optional): remote passthrough is either on or off. Rendered at the end of
 * an object's field list. Unlike a regular field, remote_fields toggles the global
 * RemoteFieldClass model, so scope/onChange are computed by the parent rather than from a field.
 */
const ScopeRemoteRow = ({
  fieldName,
  typeLabel,
  description,
  infoText,
  infoHref,
  scope,
  isViewing,
  disabled,
  disabledTooltip,
  deprecationMessage,
  isPastDeprecation = false,
  onChange,
  nested = false,
}: Props) => {
  // render
  return (
    // Baseline-align the columns (matching ScopeFieldRow) so name / type / description line up on
    // their text baseline despite differing font sizes; a wrapped description uses its first line's
    // baseline, keeping the name / type / switch pinned to the first line.
    <div className="flex items-baseline gap-8 px-8 py-3">
      <div
        className={clsx(
          "flex w-40 shrink-0 items-center gap-1.5",
          nested && "pl-6",
          isPastDeprecation && "opacity-60",
        )}
      >
        <Text variant="sm-mono" className="min-w-0 truncate text-gray-60">
          {fieldName}
        </Text>
        {infoText && (
          // leaveDelay keeps the tooltip open long enough to move into it and click the docs link.
          <Tooltip
            leaveDelay={150}
            className="inline-flex"
            tooltipClassName="max-w-xs"
            title={
              <span>
                {infoText}
                {infoHref && (
                  <>
                    {" "}
                    <Link href={infoHref} target="_blank" className="text-white underline">
                      Learn more
                    </Link>
                  </>
                )}
              </span>
            }
          >
            <span className="inline-flex shrink-0">
              <Info size={12} className="text-gray-60" />
            </span>
          </Tooltip>
        )}
      </div>
      <div className={clsx("flex w-16 shrink-0 items-center", isPastDeprecation && "opacity-60")}>
        <Text variant="sm-mono" className="text-gray-50">
          {typeLabel}
        </Text>
      </div>
      {/* Description column (also the spacer that keeps the switch right-aligned with the field
          rows above). The full text wraps onto as many lines as it needs, with the docs link flowing
          inline at the end. A deprecation message replaces the description; otherwise some rows carry
          only an info icon and have no description. */}
      <div className={clsx("min-w-0 flex-1", isPastDeprecation && "opacity-60")}>
        {deprecationMessage ? (
          <Text variant="sm" className="text-gray-50">
            {deprecationMessage}
          </Text>
        ) : (
          description && (
            <Text variant="sm" className="text-gray-50">
              {description}
              {infoHref && (
                <>
                  {" "}
                  <Link
                    href={infoHref}
                    target="_blank"
                    className="whitespace-nowrap text-sm underline"
                  >
                    Learn more
                  </Link>
                </>
              )}
            </Text>
          )
        )}
      </div>
      {/* Switch stays at full opacity (and clickable) even on a deprecated row, matching fields. */}
      <ScopePermissionSwitch
        scope={scope}
        isViewing={isViewing}
        areOptionalScopesEnabled={false}
        disabled={disabled}
        disabledTooltip={disabledTooltip}
        onChange={onChange}
      />
    </div>
  );
};

export default ScopeRemoteRow;
